import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type { Campaign, CampaignStats, CampaignStatus } from "@/types";

interface CampaignListResponse {
  campaigns: Campaign[];
  total: number;
}

export function useCampaigns(filters?: { status?: CampaignStatus | ""; search?: string }) {
  const params = new URLSearchParams();
  if (filters?.status) params.set("status", filters.status);
  if (filters?.search) params.set("search", filters.search);

  return useQuery<Campaign[]>({
    queryKey: ["campaigns", filters],
    queryFn: async () => {
      const res = await apiClient.get<CampaignListResponse>(`/campaigns?${params.toString()}`);
      return res.data.campaigns;
    },
  });
}

export function useCampaign(id: string | undefined) {
  return useQuery<Campaign>({
    queryKey: ["campaign", id],
    queryFn: async () => {
      const res = await apiClient.get<Campaign>(`/campaigns/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useCampaignStats(id: string | undefined) {
  return useQuery<CampaignStats>({
    queryKey: ["campaign-stats", id],
    queryFn: async () => {
      const res = await apiClient.get<CampaignStats>(`/campaigns/${id}/stats`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.post<Campaign>("/campaigns", data);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaigns"] }),
  });
}

export function useUpdateCampaign(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.put<Campaign>(`/campaigns/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      qc.invalidateQueries({ queryKey: ["campaign", id] });
    },
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/campaigns/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaigns"] }),
  });
}

// --- Follow-up sequence generator (Phase: webinar follow-up) ---

export interface SequenceTouch {
  day_offset: number;
  label: string;
  priority: string;
}

export interface SequenceTemplate {
  id: string;
  label: string;
  touches: SequenceTouch[];
}

export interface GenerateSequenceResult {
  created_followup_count: number;
  skipped_existing_count: number;
  individuals_processed: number;
  template: string;
}

export function useSequenceTemplates() {
  return useQuery<SequenceTemplate[]>({
    queryKey: ["campaign-sequence-templates"],
    queryFn: async () => {
      const res = await apiClient.get<{ templates: SequenceTemplate[] }>(
        "/campaigns/sequence-templates",
      );
      return res.data.templates;
    },
  });
}

export function useGenerateCampaignFollowups(campaignId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      individualIds,
      template,
    }: {
      individualIds: string[];
      template?: string;
    }) => {
      if (!campaignId) throw new Error("No campaign id");
      const res = await apiClient.post<GenerateSequenceResult>(
        `/campaigns/${campaignId}/generate-followups`,
        { individual_ids: individualIds, template: template ?? "post_webinar_4_touch" },
      );
      return res.data;
    },
    onSuccess: () => {
      // Stats panel + followups list both need refreshing
      if (campaignId) {
        qc.invalidateQueries({ queryKey: ["campaign-stats", campaignId] });
      }
      qc.invalidateQueries({ queryKey: ["crm-followups"] });
    },
  });
}
